import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    api,
    ApiError,
    newIdempotencyKey,
    type BattleHistoryItem,
    type BattleListItem,
} from '../lib/api';
import type { ViewId } from '../state';

type Props = {
    onNavigate: (id: ViewId) => void;
    onCreate: () => void;
    /** Called after a successful join so the parent can refetch balance. */
    onAfterJoin?: () => void;
    /** Bot username used to build the deep-link Telegram share URL. */
    botUsername?: string;
    /** Open the sit-in lobby for the given battle (post-create / post-join). */
    onEnterRoom?: (id: number) => void;
};

/**
 * BOG BATTLE landing.
 *
 * Lists open rooms via /api/battles?status=open with min/max/slots filters
 * applied server-side. History tab calls /api/battles?status=history. The
 * "ВОЙТИ" button hits POST /api/battles/:id/join — backend takes the stake,
 * and if the join fills the last slot, settles right there: 13% to house,
 * 1% to BIG GIVEAWAY, the rest to the winner. We surface the outcome via
 * an inline result line on the row.
 */
type Scope = 'all' | 'mine';

export function BogBattleView({
    onNavigate, onCreate, onAfterJoin, myId, botUsername, onEnterRoom,
}: Props & { myId?: number | null }) {
    const shareBattle = async (b: BattleListItem) => {
        // Preferred: Bot API 8.0 prepareInlineMessage + WebApp.shareMessage.
        // This is the only path that puts a real "ВОЙТИ В БАТЛ" inline button
        // under the message — t.me/share/url can only carry raw text and a
        // link, no inline keyboard, which is what made the previous share
        // arrive without any buttons.
        const webApp = window.Telegram?.WebApp;
        if (webApp?.shareMessage) {
            try {
                const r = await api.prepareBattleShare(b.id);
                webApp.shareMessage(r.preparedMessageId);
                return;
            } catch {
                // Backend couldn't prepare (e.g. bot lacks scope, network
                // hiccup, or this Telegram client lied about shareMessage).
                // Fall through to the legacy share-url path.
            }
        }

        const deeplink = `https://t.me/${botUsername ?? 'BogStarsBot'}?startapp=battle_${b.id}`;
        const need = Math.max(0, b.maxSlots - b.joined);
        const text =
            `⚔️ Бросаю вызов в BOG BATTLE!\n` +
            `Ставка: ${b.stake} BSP · Игроков: ${b.joined}/${b.maxSlots}` +
            (need > 0 ? ` (нужно ещё ${need})` : ' · мест нет') +
            `\nЗаходи и забирай банк 👇`;
        const shareUrl = `https://t.me/share/url?url=${encodeURIComponent(deeplink)}` +
                         `&text=${encodeURIComponent(text)}`;
        if (webApp?.openTelegramLink) {
            webApp.openTelegramLink(shareUrl);
        } else {
            window.open(shareUrl, '_blank', 'noopener,noreferrer');
        }
    };
    const [min, setMin] = useState('');
    // History used to sit in a 130px scroll box; now the page scrolls, so
    // show the latest few and let the rest unfold on demand.
    const [showAllHistory, setShowAllHistory] = useState(false);
    const [max, setMax] = useState('');
    const [sort, setSort] = useState<'desc' | 'asc'>('desc');
    const [size, setSize] = useState<'all' | '2' | '3' | '4' | '5' | '6'>('all');
    const [scope, setScope] = useState<Scope>('all');

    const [items, setItems] = useState<BattleListItem[]>([]);
    const [history, setHistory] = useState<BattleHistoryItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [busyId, setBusyId] = useState<number | null>(null);
    const [outcomeMsg, setOutcomeMsg] = useState<string | null>(null);

    const reload = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [open, hist] = await Promise.all([
                api.listBattles({
                    status: 'open',
                    min: numOrU(min),
                    max: numOrU(max),
                    slots: size === 'all' ? undefined : Number(size),
                }),
                api.listBattles({ status: 'history' }),
            ]);
            setItems(open.items as BattleListItem[]);
            setHistory(hist.items as BattleHistoryItem[]);
        } catch (e) {
            // Backend may legitimately be unreachable in dev / offline. Show
            // a soft error rather than blanking the screen.
            setError(e instanceof ApiError
                ? `${e.status}: ${e.code}${e.detail ? ` — ${e.detail}` : ''}`
                : 'Не удалось загрузить батлы.');
        } finally {
            setLoading(false);
        }
    }, [min, max, size]);

    useEffect(() => { void reload(); }, [reload]);

    const filtered = useMemo(() => {
        let arr = [...items];
        if (scope === 'mine' && myId) {
            arr = arr.filter((b) =>
                b.creator.id === myId ||
                (b.participants ?? []).some((p) => p.userId === myId),
            );
        }
        arr.sort((a, b) => (sort === 'desc' ? b.stake - a.stake : a.stake - b.stake));
        return arr;
    }, [items, sort, scope, myId]);

    // VIP / pinned battles get their own ribbon above the search list per ТЗ
    // (BattleBSP.md: "сделать в главном разделе еще до поиска батлов 'VIP
    // батлы'"). We never filter VIPs by scope/min/max so they stay visible.
    const vipItems = useMemo(
        () => items.filter((b) => b.vip || b.pinned)
                   .sort((a, b) => b.stake - a.stake),
        [items],
    );
    const regularItems = useMemo(
        () => filtered.filter((b) => !(b.vip || b.pinned)),
        [filtered],
    );

    const join = async (b: BattleListItem) => {
        setBusyId(b.id);
        setOutcomeMsg(null);
        try {
            const res = await api.joinBattle(b.id, newIdempotencyKey());
            onAfterJoin?.();
            // Drop the user into the sit-in lobby — they either watch the
            // last slots fill, or see the win/lose reveal that just happened.
            if (onEnterRoom) {
                onEnterRoom(b.id);
            } else if (res.finished && res.outcome) {
                setOutcomeMsg(`Батл #${b.id} завершён. Выплата: ${res.outcome.payout} BSP.`);
                await reload();
            } else {
                setOutcomeMsg(`Вы зашли в батл #${b.id}. Ждём ${b.maxSlots - res.joined} участников.`);
                await reload();
            }
        } catch (e) {
            setOutcomeMsg(e instanceof ApiError ? translate(e.code) : 'Не удалось войти в батл.');
        } finally {
            setBusyId(null);
        }
    };

    return (
        <div className="view">
            <h2 className="text-center fw-900 text-red" style={{ fontSize: 18, letterSpacing: 2 }}>
                BOG BATTLE
            </h2>

            <div style={{ display: 'flex', gap: 8 }}>
                <button type="button" className="action-btn btn-red" style={{ flex: 1 }} onClick={() => void reload()}>
                    {loading ? 'ОБНОВЛЯЕМ…' : 'ОБНОВИТЬ СПИСОК'}
                </button>
                <button
                    type="button"
                    className="action-btn btn-outline-gold"
                    style={{ flex: 1 }}
                    onClick={onCreate}
                >
                    СОЗДАТЬ БАТЛ
                </button>
            </div>

            <div className="tabs" role="tablist" style={{ marginTop: 4 }}>
                <button
                    type="button"
                    className={`tab-btn ${scope === 'all' ? 'active' : ''}`}
                    onClick={() => setScope('all')}
                >
                    Все
                </button>
                <button
                    type="button"
                    className={`tab-btn ${scope === 'mine' ? 'active' : ''}`}
                    onClick={() => setScope('mine')}
                    disabled={!myId}
                    title={myId ? '' : 'Откройте Mini App в Telegram'}
                >
                    Мои
                </button>
            </div>

            <div className="info-card" style={{ padding: 8 }}>
                <div className="filter-row">
                    <input className="filter-input" type="number" placeholder="Мин. цена" value={min} onChange={(e) => setMin(e.target.value)} />
                    <input className="filter-input" type="number" placeholder="Макс. цена" value={max} onChange={(e) => setMax(e.target.value)} />
                </div>
                <div className="filter-row">
                    <select className="filter-input" value={sort} onChange={(e) => setSort(e.target.value as 'desc' | 'asc')}>
                        <option value="desc">Сначала дорогие</option>
                        <option value="asc">Сначала дешевые</option>
                    </select>
                    <select className="filter-input" value={size} onChange={(e) => setSize(e.target.value as typeof size)}>
                        <option value="all">Все размеры</option>
                        <option value="2">2 участника</option>
                        <option value="3">3 участника</option>
                        <option value="4">4 участника</option>
                        <option value="5">5 участников</option>
                        <option value="6">6 участников</option>
                    </select>
                </div>
            </div>

            {outcomeMsg && (
                <div className="info-card">
                    <p className="popup-text">{outcomeMsg}</p>
                </div>
            )}
            {error && (
                <div className="info-card" style={{ borderColor: 'var(--red)' }}>
                    <p className="popup-text" style={{ color: 'var(--red)' }}>{error}</p>
                </div>
            )}

            {vipItems.length > 0 && (
                <>
                    <h3 className="text-muted fw-900 f-12" style={{ textTransform: 'uppercase', color: 'var(--accent)' }}>
                        ⭐ VIP батлы
                    </h3>
                    <div className="battle-slider">
                        {vipItems.map((b) => renderBattleCard(b))}
                    </div>
                </>
            )}

            <h3 className="text-muted fw-900 f-12" style={{ textTransform: 'uppercase' }}>
                Активные батлы
            </h3>
            <div className="battle-slider">
                {regularItems.length === 0 && !loading && (
                    <div className="text-muted f-12 text-center" style={{ padding: 12, width: '100%' }}>
                        Нет активных батлов под фильтр.
                    </div>
                )}
                {regularItems.map((b) => renderBattleCard(b))}
            </div>

            <h3 className="text-muted fw-900 f-12" style={{ textTransform: 'uppercase' }}>
                История батлов
            </h3>
            <div className="info-card" style={{ padding: '4px 12px' }}>
                {history.length === 0 && (
                    <div className="text-muted f-12 text-center" style={{ padding: 12 }}>
                        У вас ещё нет завершённых батлов.
                    </div>
                )}
                {(showAllHistory ? history : history.slice(0, 10)).map((h) => (
                    <HistoryRow
                        key={h.id}
                        date={fmtShort(h.created_at)}
                        delta={`${h.won ? '+' : '-'}${h.stake} BSP`}
                        win={h.won}
                    />
                ))}
                {history.length > 10 && (
                    <button type="button" className="action-btn btn-mute" style={{ margin: '8px 0', fontSize: 11 }}
                        onClick={() => setShowAllHistory((v) => !v)}>
                        {showAllHistory ? 'Свернуть' : `Показать все (${history.length})`}
                    </button>
                )}
            </div>

            <button type="button" className="back-btn" onClick={() => onNavigate('games')}>
                НАЗАД В ИГРЫ
            </button>
        </div>
    );

    function renderBattleCard(b: BattleListItem) {
        const accent = b.pinned || b.vip;
        // Creator label: real handle if not anon, otherwise the
        // deterministic emoji + "Аноним" per ТЗ.
        const handle = b.creator.anon
            ? `${b.creator.emoji ?? '🎭'} Аноним`
            : (b.creator.username
                ? '@' + b.creator.username
                : (b.creator.firstName ?? `id ${b.creator.id}`));
        return (
            <div key={b.id} className={`battle-card ${accent ? 'gold' : ''}`}>
                <div className="b-author" style={{ color: accent ? 'var(--accent)' : 'var(--text-muted)' }}>
                    {handle}
                </div>
                <div className="b-bet" style={{ color: accent ? '#fff' : 'var(--red)' }}>
                    {b.stake} BSP
                </div>
                <div className="b-wait">
                    Ждём: {b.joined}/{b.maxSlots}
                </div>
                {b.participants && b.participants.length > 0 && (
                    <div
                        className="b-roster"
                        aria-label="Участники"
                        title={b.participants
                            .map((p) => p.anon
                                ? 'Аноним'
                                : (p.username ? '@' + p.username : (p.firstName ?? `id${p.userId}`)))
                            .join(', ')}
                    >
                        {b.participants.map((p) => (
                            <span
                                key={p.userId}
                                className={`b-roster-slot${p.anon ? ' anon' : ''}${p.isCreator ? ' creator' : ''}`}
                            >
                                {p.anon ? p.emoji : (p.username ? '@' + p.username.slice(0, 6) : p.emoji)}
                            </span>
                        ))}
                        {Array.from({ length: Math.max(0, b.maxSlots - b.participants.length) }).map((_, i) => (
                            <span key={`empty-${i}`} className="b-roster-slot empty">?</span>
                        ))}
                    </div>
                )}
                <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
                    <button
                        type="button"
                        className={`action-btn ${accent ? 'btn-gold' : 'btn-red'}`}
                        style={{ padding: 6, fontSize: 9, flex: 1 }}
                        onClick={() => void join(b)}
                        disabled={busyId === b.id}
                    >
                        {busyId === b.id ? '…' : 'ВОЙТИ'}
                    </button>
                    <button
                        type="button"
                        className="action-btn btn-outline-gold"
                        style={{ padding: 6, fontSize: 9, width: 32 }}
                        onClick={() => shareBattle(b)}
                        aria-label="Поделиться батлом"
                        title="Поделиться"
                    >
                        ↗
                    </button>
                </div>
            </div>
        );
    }
}

function HistoryRow({ date, delta, win }: { date: string; delta: string; win: boolean }) {
    return (
        <div className="top-row">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                <span className="top-name fw-900" style={{ fontSize: 11 }}>Батл #{date}</span>
            </div>
            <span className="fw-900 f-12" style={{ color: win ? 'var(--green)' : 'var(--red)' }}>
                {delta}
            </span>
        </div>
    );
}

function numOrU(s: string): number | undefined {
    const n = Number(s);
    return Number.isFinite(n) && s !== '' ? n : undefined;
}
function fmtShort(iso: string) {
    try { return new Date(iso).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' }); }
    catch { return iso; }
}
function translate(code: string) {
    switch (code) {
        case 'insufficient_balance': return 'Недостаточно BSP.';
        case 'not_open':             return 'Батл уже стартовал или завершён.';
        case 'full':                 return 'Все слоты заняты.';
        case 'already_joined':       return 'Вы уже участвуете в этом батле.';
        case 'too_many_requests':    return 'Слишком часто. Подождите пару секунд.';
        default:                     return 'Не удалось войти (' + code + ').';
    }
}
